import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../api/educentialApi';

const ServicePortal = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrollingService, setEnrollingService] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.getAllServices();
      setServices(response.data);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (serviceId) => {
    try {
      setEnrollingService(serviceId);
      await api.enrollInService(serviceId);
      // Refresh services to update enrollment status
      await fetchServices();
      // Navigate to service if it's active
      const service = services.find(s => s.id === serviceId);
      if (service && service.status === 'active') {
        navigate(`/services/${serviceId}`);
      }
    } catch (err) {
      console.error('Error enrolling in service:', err);
      alert('Failed to enroll in service. Please try again.');
    } finally {
      setEnrollingService(null);
    }
  };

  const handleLaunchService = async (service) => {
    if (service.status !== 'active') {
      alert('This service is coming soon!');
      return;
    }

    // If not enrolled, enroll first
    if (!service.enrolled) {
      await handleEnroll(service.id);
    } else {
      // Already enrolled, navigate to service
      navigate(`/services/${service.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchServices}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Group services by category
  const servicesByCategory = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  const categoryNames = {
    education: 'Education',
    career: 'Career Development',
    travel: 'Travel & Accommodation',
    support: 'Support Services',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">All Services</h1>
          <p className="text-gray-600 mt-1">
            Explore and enroll in our comprehensive suite of educational and career services
          </p>
        </div>

        {/* Services by Category */}
        {Object.entries(servicesByCategory).map(([category, categoryServices]) => (
          <div key={category} className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              {categoryNames[category] || category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  enrolling={enrollingService === service.id}
                  onLaunch={handleLaunchService}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, enrolling, onLaunch }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'beta':
        return 'bg-blue-100 text-blue-800';
      case 'coming-soon':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'beta':
        return 'Beta';
      case 'coming-soon':
        return 'Coming Soon';
      default:
        return status;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden">
      <div className="p-6">
        {/* Service Icon and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="text-4xl">{service.icon}</div>
          <div className="flex flex-col items-end gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
              {getStatusText(service.status)}
            </span>
            {service.enrolled && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Enrolled
              </span>
            )}
          </div>
        </div>

        {/* Service Name */}
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h3>

        {/* Service Description */}
        <p className="text-sm text-gray-600 mb-4">{service.description}</p>

        {/* Last Accessed (if enrolled) */}
        {service.enrolled && service.lastAccessed && (
          <p className="text-xs text-gray-500 mb-4">
            Last accessed: {new Date(service.lastAccessed).toLocaleDateString()}
          </p>
        )}

        {/* Action Button */}
        <button
          onClick={() => onLaunch(service)}
          disabled={enrolling}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
            service.status === 'active'
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-600 cursor-not-allowed'
          } ${enrolling ? 'opacity-50 cursor-wait' : ''}`}
        >
          {enrolling ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Enrolling...
            </span>
          ) : service.enrolled ? (
            'Continue'
          ) : service.status === 'active' ? (
            'Get Started'
          ) : (
            'Coming Soon'
          )}
        </button>
      </div>
    </div>
  );
};

export default ServicePortal;
